/**
 * enemies.ts — Enemy definitions for the tactical combat system.
 *
 * 19 enemies across 4 acts + 4 bosses.
 * Balanced for deterministic combat (no dice rolls — stat-based resolution).
 */

import type { EnemyDefinition } from '@core/combat/CombatTypes';

const enemies: EnemyDefinition[] = [
  // ─── Act 1 ──────────────────────────────────────────────
  {
    id: 'skeletal_warrior',
    name: 'Skeletal Warrior',
    hp: 15,
    attack: 5,
    defense: 2,
    xpReward: 50,
    goldReward: 10,
    behavior: 'aggressive',
    act: 1,
    isBoss: false,
    loot: [],
    description: 'A reanimated warrior clutching a rusted blade. Its bones rattle with each step.',
    frostbiteDamage: 0,
  },
  {
    id: 'wight_soldier',
    name: 'Wight Soldier',
    hp: 20,
    attack: 6,
    defense: 3,
    xpReward: 65,
    goldReward: 15,
    behavior: 'balanced',
    act: 1,
    isBoss: false,
    loot: [],
    description: 'A preserved corpse still wearing its armor, driven by dark purpose.',
    frostbiteDamage: 1,
  },
  {
    id: 'corrupted_guard',
    name: 'Corrupted Guard',
    hp: 22,
    attack: 7,
    defense: 4,
    xpReward: 75,
    goldReward: 20,
    behavior: 'defensive',
    act: 1,
    isBoss: false,
    loot: [],
    description: 'A former Gallowcrest guard, now a puppet of the Lich King\'s will.',
    frostbiteDamage: 0,
  },
  {
    id: 'frost_wolf',
    name: 'Frost Wolf',
    hp: 12,
    attack: 8,
    defense: 1,
    xpReward: 55,
    goldReward: 5,
    behavior: 'cowardly',
    act: 1,
    isBoss: false,
    loot: [],
    description: 'A snarling wolf with frost-rimmed fur and icy blue eyes.',
    frostbiteDamage: 2,
  },

  // ─── Act 2 ──────────────────────────────────────────────
  {
    id: 'bone_knight',
    name: 'Bone Knight',
    hp: 30,
    attack: 9,
    defense: 5,
    xpReward: 100,
    goldReward: 25,
    behavior: 'balanced',
    act: 2,
    isBoss: false,
    loot: [],
    description: 'An elite undead warrior encased in bone plate. Methodical and relentless.',
    frostbiteDamage: 1,
  },
  {
    id: 'dark_acolyte',
    name: 'Dark Acolyte',
    hp: 18,
    attack: 10,
    defense: 3,
    xpReward: 90,
    goldReward: 30,
    behavior: 'aggressive',
    act: 2,
    isBoss: false,
    loot: [],
    description: 'A robed figure channeling necrotic energy. Dangerous but fragile.',
    frostbiteDamage: 0,
  },
  {
    id: 'corrupted_captain',
    name: 'Corrupted Captain',
    hp: 35,
    attack: 8,
    defense: 6,
    xpReward: 110,
    goldReward: 35,
    behavior: 'defensive',
    act: 2,
    isBoss: false,
    loot: [],
    description: 'An officer twisted by dark magic, still barking orders to the dead.',
    frostbiteDamage: 0,
  },
  {
    id: 'ice_wraith',
    name: 'Ice Wraith',
    hp: 22,
    attack: 11,
    defense: 2,
    xpReward: 95,
    goldReward: 20,
    behavior: 'aggressive',
    act: 2,
    isBoss: false,
    loot: [],
    description: 'A translucent spirit of frozen malice. Its touch burns with cold.',
    frostbiteDamage: 3,
  },

  // ─── Act 3 ──────────────────────────────────────────────
  {
    id: 'death_knight',
    name: 'Death Knight',
    hp: 45,
    attack: 12,
    defense: 7,
    xpReward: 150,
    goldReward: 40,
    behavior: 'balanced',
    act: 3,
    isBoss: false,
    loot: [],
    description: 'A fallen paladin, now a champion of undeath. Its dark aura weakens the living.',
    frostbiteDamage: 2,
  },
  {
    id: 'necromantic_horror',
    name: 'Necromantic Horror',
    hp: 35,
    attack: 14,
    defense: 4,
    xpReward: 140,
    goldReward: 35,
    behavior: 'aggressive',
    act: 3,
    isBoss: false,
    loot: [],
    description: 'A grotesque fusion of corpses animated by raw necromantic power.',
    frostbiteDamage: 1,
  },
  {
    id: 'corrupted_mage',
    name: 'Corrupted Mage',
    hp: 28,
    attack: 15,
    defense: 5,
    xpReward: 160,
    goldReward: 50,
    behavior: 'defensive',
    act: 3,
    isBoss: false,
    loot: [],
    description: 'A former court wizard consumed by forbidden knowledge.',
    frostbiteDamage: 0,
  },
  {
    id: 'shadow_beast',
    name: 'Shadow Beast',
    hp: 32,
    attack: 13,
    defense: 3,
    xpReward: 130,
    goldReward: 25,
    behavior: 'cowardly',
    act: 3,
    isBoss: false,
    loot: [],
    description: 'A creature of living shadow that strikes from the darkness.',
    frostbiteDamage: 2,
  },

  // ─── Act 4 ──────────────────────────────────────────────
  {
    id: 'lich_servant',
    name: 'Lich Servant',
    hp: 50,
    attack: 15,
    defense: 8,
    xpReward: 200,
    goldReward: 60,
    behavior: 'balanced',
    act: 4,
    isBoss: false,
    loot: [],
    description: 'A powerful undead mage bound to the Lich King\'s will.',
    frostbiteDamage: 3,
  },
  {
    id: 'frost_abomination',
    name: 'Frost Abomination',
    hp: 60,
    attack: 14,
    defense: 6,
    xpReward: 180,
    goldReward: 45,
    behavior: 'aggressive',
    act: 4,
    isBoss: false,
    loot: [],
    description: 'A monstrous golem of ice and bone, nearly unstoppable.',
    frostbiteDamage: 4,
  },
  {
    id: 'shadow_commander',
    name: 'Shadow Commander',
    hp: 55,
    attack: 16,
    defense: 9,
    xpReward: 220,
    goldReward: 70,
    behavior: 'defensive',
    act: 4,
    isBoss: false,
    loot: [],
    description: 'The Lich King\'s top lieutenant, a master tactician even in undeath.',
    frostbiteDamage: 2,
  },

  // ─── Bosses ─────────────────────────────────────────────
  {
    id: 'boss_valdris',
    name: 'Lord Valdris',
    hp: 40,
    attack: 10,
    defense: 6,
    xpReward: 200,
    goldReward: 100,
    behavior: 'boss',
    act: 1,
    isBoss: true,
    loot: ['valdris_signet'],
    description: 'The corrupted Lord of Gallowcrest, wielding dark authority.',
    frostbiteDamage: 2,
  },
  {
    id: 'boss_bone_lord',
    name: 'The Bone Lord',
    hp: 60,
    attack: 13,
    defense: 7,
    xpReward: 350,
    goldReward: 150,
    behavior: 'boss',
    act: 2,
    isBoss: true,
    loot: ['bone_lord_crown'],
    description: 'An ancient undead general entombed for centuries, now risen to command.',
    frostbiteDamage: 3,
  },
  {
    id: 'boss_necromancer_general',
    name: 'Necromancer General',
    hp: 80,
    attack: 16,
    defense: 8,
    xpReward: 500,
    goldReward: 200,
    behavior: 'boss',
    act: 3,
    isBoss: true,
    loot: ['dark_phylactery'],
    description: 'The mastermind behind the undead invasion, channeling the Lich King\'s power.',
    frostbiteDamage: 2,
  },
  {
    id: 'boss_lich_king',
    name: 'The Lich King',
    hp: 120,
    attack: 20,
    defense: 10,
    xpReward: 1000,
    goldReward: 500,
    behavior: 'boss',
    act: 4,
    isBoss: true,
    loot: ['frostcrown_shard'],
    description: 'The master of undeath, enthroned in eternal winter. The final challenge.',
    frostbiteDamage: 5,
  },
];

/** Get enemy by ID. */
export function getEnemy(id: string): EnemyDefinition | undefined {
  return enemies.find(e => e.id === id);
}

/** Get all enemies for a given act. */
export function getEnemiesByAct(act: number): EnemyDefinition[] {
  return enemies.filter(e => e.act === act && !e.isBoss);
}

/** Get the boss for a given act. */
export function getBoss(act: number): EnemyDefinition | undefined {
  return enemies.find(e => e.act === act && e.isBoss);
}

/** Get all enemy definitions. */
export function getAllEnemies(): EnemyDefinition[] {
  return [...enemies];
}
